import type { IntlShape } from 'react-intl';

import { NavLink } from '~/eds/src/types/NavLink';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { SavedSearch } from '~/features/search/types/search';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsInternalSuperAdmin } from '~/permissions/checkIsInternalSuperAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { checkIsRbacAdmin } from '~/permissions/rbac/checkIsRbacAdmin';
import { DashboardAndPathname } from '~/redux/api/methods/dashboardV2';
import { RoutePathType } from '~/routing/enums';
import { DashboardTab } from '~/types/Dashboard';
import { User } from '~/types/User';

import { featureFlagIncluded } from './user';

interface Permissions {
  documents?: boolean;
  documentGroups?: boolean;
  entityGroups?: boolean;
  workflowTickets?: boolean;
  workflowBuilder?: boolean;
  clauseLibrary?: boolean;
  automation?: boolean;
  documentXRay?: boolean;
  documentsDashboard?: boolean;
  workflowsDashboard?: boolean;
  managedEntityParties?: boolean;
}

export const createNavlinks = (
  currentUser: User,
  intl: IntlShape,
  onNavLinkClick: (pathname: string) => void,
  dashboards?: DashboardTab[],
  activeSearch?: SavedSearch,
  hasClauseLibrary?: Boolean,
  hasDeprecateAnalyzer?: boolean,
  hasPlaybooks?: boolean,
  hasWorkflowRoutingAgent?: boolean,
  activeDashboard?: DashboardAndPathname,
  hasGroupedTicketsAndDocs?: boolean,
  hasClmEvolutionPhase1?: boolean,
  permissions: Permissions = {},
) => {
  const navLinks: NavLink[] = [];
  if (checkIsWorkflowOnly(currentUser)) {
    if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
      return [
        {
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflowTickets',
            defaultMessage: 'Workflow',
            description: 'Workflow navigation link',
          }),
          pathname: RoutePathType.WorkflowTickets,
          children: [],
        },
      ];
    }
    return [];
  }

  if (!featureFlagIncluded(currentUser, 'REVIEWER')) {
    const shouldShowDocumentsNav = hasGroupedTicketsAndDocs
      ? permissions['documents']
      : permissions['documents'] || permissions['documentGroups'];

    const shouldShowDocumentGroupsSubnav = hasGroupedTicketsAndDocs
      ? permissions['documents'] && permissions['entityGroups']
      : permissions['documents'] && permissions['documentGroups'];

    const groupListRoute = hasGroupedTicketsAndDocs
      ? RoutePathType.DocumentGroupsNew
      : RoutePathType.DocumentGroups;

    if (shouldShowDocumentsNav) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.documents',
          defaultMessage: 'Documents',
          description: 'Documents navigation link',
        }),
        pathname: permissions['documents']
          ? RoutePathType.Documents
          : groupListRoute,
        children: shouldShowDocumentGroupsSubnav
          ? createDocumentSubNavLinks(currentUser, groupListRoute, intl)
          : [],
      });
    }

    // temporary, should not be a top level navlink
    // incorporate with Documents
    if (permissions['managedEntityParties']) {
      if (canUseManagedEntities(currentUser)) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.parties',
            defaultMessage: 'Parties',
            description: 'Parties navigation link',
          }),
          pathname: RoutePathType.ManagedEntities,
        });
      }
    }

    if (permissions['documents']) {
      if (featureFlagIncluded(currentUser, FeatureFlagType.SearchV2)) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.search',
            defaultMessage: 'Search',
            description: 'Search navigation link',
          }),
          pathname: RoutePathType.Search,
          children: createSearchSubNavLinks({
            activeSearch,
            hasDeprecateAnalyzer,
            onNavLinkClick,
            intl,
          }),

          onClick: () => onNavLinkClick(RoutePathType.Search),
        });
      } else {
        if (!hasDeprecateAnalyzer) {
          navLinks.push({
            label: intl.formatMessage({
              id: 'PageHeader.navLinks.analyzer',
              defaultMessage: 'Analyzer',
              description: 'Analyzer navigation link',
            }),
            pathname: RoutePathType.Analyzer,
            children: [],
          });
        }
      }
    }

    if (
      permissions['documentsDashboard'] ||
      permissions['workflowsDashboard']
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.dashboards',
          defaultMessage: 'Dashboards',
          description: 'Dashboards navigation link',
        }),
        pathname: '/dashboards',
        children: createDashboardsSubNavLinks(
          currentUser,
          RoutePathType.Dashboards,
          intl,
          dashboards,
          onNavLinkClick,
          activeDashboard,
        ),
      });
    }

    const workflowPermissionCount = [
      permissions['workflowTickets'],
      permissions['workflowBuilder'],
      permissions['entityGroups'] && hasGroupedTicketsAndDocs,
    ].filter(Boolean).length;

    if (permissions['workflowTickets'] || permissions['workflowBuilder']) {
      if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
        const shouldShowSubNav = workflowPermissionCount > 1;
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflow',
            defaultMessage: 'Workflow',
            description: 'Workflow navigation link',
          }),
          pathname: permissions['workflowTickets']
            ? RoutePathType.WorkflowTickets
            : RoutePathType.WorkflowBuilder,
          children: shouldShowSubNav
            ? createWorkflowSubNavLinks(
                currentUser,
                intl,
                permissions,
                hasGroupedTicketsAndDocs ?? false,
              )
            : [],
        });
      }
    }
    if (
      (permissions['workflowTickets'] || permissions['workflowBuilder']) &&
      hasClmEvolutionPhase1
    ) {
      const shouldShowSubNav = workflowPermissionCount > 1;
      navLinks.push({
        label: 'Workflows CLMe',
        pathname: permissions['workflowTickets']
          ? RoutePathType.WorkflowCLMEvolutionTickets
          : RoutePathType.WorkflowCLMEvolutionWorkflows,
        children: shouldShowSubNav
          ? createWorkflowCLMEvolutionSubNavLinks(
              currentUser,
              intl,
              permissions,
            )
          : [],
      });
    }

    if (permissions['clauseLibrary']) {
      if (hasClauseLibrary) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.clauseLibrary',
            defaultMessage: 'Library',
            description: 'Clause Library navigation link',
          }),
          pathname: hasPlaybooks ? '/playbooks' : '/clause-library',
          children: hasPlaybooks ? createLibrarySubNavLinks(intl) : [],
          activePatterns: hasPlaybooks
            ? [
                '^/playbooks(/.*)?$',
                '^/guidelines(/.*)?$',
                '^/clause-library(/.*)?$',
              ]
            : ['^/clause-library(/.*)?$'],
        });
      }
    }

    if (permissions['documents']) {
      if (featureFlagIncluded(currentUser, 'UPLOAD')) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.upload',
            defaultMessage: 'Upload',
            description: 'Upload navigation link',
          }),
          pathname: RoutePathType.Upload,
          children: [],
        });
      }
    }

    const hasAutomationPermission = permissions['automation'];
    const hasDocumentXRayPermission = permissions['documentXRay'];
    const hasWorkflowBuilderPermission = permissions['workflowBuilder'];
    const hasWorkflowRoutingAccess =
      hasWorkflowBuilderPermission && hasWorkflowRoutingAgent;

    const hasAnyAutomationPermission =
      hasAutomationPermission ||
      hasDocumentXRayPermission ||
      hasWorkflowRoutingAccess;

    if (hasAnyAutomationPermission) {
      const canAccessViaAutomation = canUseAutomation(currentUser);
      const canAccessViaWorkflowBuilder =
        canUseWorkflowAdmin(currentUser) && hasWorkflowBuilderPermission;
      const canAccessAutomation =
        canAccessViaAutomation || canAccessViaWorkflowBuilder;

      if (canAccessAutomation) {
        const getDefaultPathname = (): string => {
          if (hasDocumentXRayPermission) {
            return RoutePathType.AutomationHubFields;
          }
          if (
            hasWorkflowBuilderPermission &&
            !hasAutomationPermission &&
            !hasDocumentXRayPermission &&
            hasWorkflowRoutingAgent
          ) {
            return RoutePathType.RoutingTable;
          }
          return RoutePathType.AutomationHub;
        };

        const shouldShowSubNavLinks =
          (hasAutomationPermission && hasDocumentXRayPermission) ||
          hasWorkflowRoutingAccess;

        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.automation',
            defaultMessage: 'Automation',
            description: 'Automation navigation link',
          }),
          pathname: getDefaultPathname(),
          children: shouldShowSubNavLinks
            ? createAutomationSubNavLinks(
                permissions,
                intl,
                hasWorkflowRoutingAgent,
              )
            : [],
        });
      }
    }

    if (
      checkIsInternalSuperAdmin(currentUser) ||
      featureFlagIncluded(currentUser, 'ADMIN') ||
      checkIsRbacAdmin(currentUser)
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.adminConsole',
          defaultMessage: 'Admin',
          description: 'Admin Console navigation link',
        }),
        pathname: `/admin/console/${currentUser.client}`,
        children: [],
      });
    }
  }

  return navLinks;
};

export const createWorkflowSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
  hasGroupedTicketsAndDocs: boolean,
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowTickets',
        defaultMessage: 'Tickets',
        description: 'Workflow Tickets navigation link',
      }),
      pathname: RoutePathType.WorkflowTickets,
      activePatterns: ['^/workflow/tickets/.+'],
    });
  }

  if (hasGroupedTicketsAndDocs && permissions.entityGroups) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowGroups',
        defaultMessage: 'Groups',
        description: 'Workflow Groups navigation link',
      }),
      pathname: RoutePathType.WorkflowGroups,
      activePatterns: ['^/workflow/groups/.+'],
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowBuilder',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow Builder navigation link',
      }),
      pathname: RoutePathType.WorkflowBuilder,
    });
  }

  return links;
};

export const createWorkflowCLMEvolutionSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowTickets',
        defaultMessage: 'Tickets',
        description: 'Workflow Tickets navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionTickets,
      activePatterns: ['^/workflows-clm-evolution/tickets/.+'],
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowBuilder',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow Builder navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionWorkflows,
      activePatterns: ['^/workflows-clm-evolution/workflows/.+'],
    });
  }

  return links;
};

export const createDocumentSubNavLinks = (
  currentUser: User,
  pathname: string,
  intl: IntlShape,
): NavLink[] => {
  if (shouldShowDocumentSubNavBar(currentUser, pathname)) {
    return [
      {
        label: intl.formatMessage({
          id: 'PageHeader.subNavLinks.allDocuments',
          defaultMessage: 'All Documents',
          description: 'All Documents navigation link',
        }),
        pathname: RoutePathType.Documents,
        activePatterns: ['^/documents/\\d+'],
      },

      {
        label: intl.formatMessage({
          id: 'PageHeader.subNavLinks.documentGroups',
          defaultMessage: 'Groups',
          description: 'Document Groups navigation link',
        }),
        pathname,
        activePatterns: ['^/document-group/\\d+', '^/documents/groups/\\d+'],
      },
    ];
  }

  return [];
};

export const createAutomationSubNavLinks = (
  permissions: Permissions,
  intl: IntlShape,
  hasWorkflowRoutingAgent?: boolean,
) => {
  const subnavLinks = [];
  if (permissions['documentXRay']) {
    subnavLinks.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.fields',
        defaultMessage: 'Fields',
        description: 'Fields automation navigation link',
      }),
      pathname: RoutePathType.AutomationHubFields,
    });
  }
  if (permissions['automation']) {
    subnavLinks.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.automation',
        defaultMessage: 'Clauses',
        description: 'Clause automation navigation link',
      }),
      pathname: RoutePathType.AutomationHub,
    });
  }
  if (hasWorkflowRoutingAgent && permissions['workflowBuilder']) {
    subnavLinks.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.routingTable',
        defaultMessage: 'Routing Table',
        description: 'Routing Table navigation link',
      }),
      pathname: RoutePathType.RoutingTable,
    });
  }
  return subnavLinks;
};

export const createSearchSubNavLinks = ({
  intl,
  activeSearch,
  hasDeprecateAnalyzer,
  onNavLinkClick,
}: {
  intl: IntlShape;
  activeSearch?: SavedSearch;
  hasDeprecateAnalyzer?: boolean;
  onNavLinkClick?: (pathname: string) => void;
}): NavLink[] => {
  const searchSubNavs: NavLink[] = [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.search',
        defaultMessage: 'Search',
        description: 'Search navigation link',
      }),
      pathname: RoutePathType.Search,
      onClick: () => onNavLinkClick?.(RoutePathType.Search),
    },

    ...(hasDeprecateAnalyzer
      ? []
      : [
          {
            label: intl.formatMessage({
              id: 'PageHeader.subNavLinks.analyzer',
              defaultMessage: 'Analyzer',
              description: 'Analyzer navigation link',
            }),
            pathname: RoutePathType.Analyzer,
            onClick: () => onNavLinkClick?.(RoutePathType.Analyzer),
          },
        ]),

    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.savedSearches',
        defaultMessage: 'Saved Searches',
        description: 'Saved Searches navigation link',
      }),
      pathname: RoutePathType.SavedSearches,
      onClick: () => onNavLinkClick?.(RoutePathType.SavedSearches),
    },
  ];

  if (activeSearch) {
    searchSubNavs.push({
      label: activeSearch.name,
      onClick: () =>
        onNavLinkClick?.(
          RoutePathType.SavedSearch.replace(':searchId', `${activeSearch.id}`),
        ),

      pathname: RoutePathType.SavedSearch.replace(
        ':searchId',
        `${activeSearch.id}`,
      ),
    });
  }

  searchSubNavs.push({
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.alerts',
      defaultMessage: 'Alerts',
      description: 'Alerts navigation link',
    }),
    pathname: RoutePathType.Alerts,
  });
  return searchSubNavs;
};

export const createDashboardsSubNavLinks = (
  currentUser: User,
  pathname: string,
  intl: IntlShape,
  dashboards?: any[],
  onNavLinkClick?: (pathname: string) => void,
  activeDashboard?: DashboardAndPathname,
): NavLink[] => {
  const dashboardSubNavs = (dashboards || []).map((link: DashboardTab) => {
    const pathname = link.is_default
      ? `/dashboards/${link.id}`
      : `/dashboards/${link.type}`;

    return {
      label: link.name,
      onClick: () => onNavLinkClick?.(pathname),
      pathname,
    };
  });

  dashboardSubNavs.push({
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.savedDashboards',
      defaultMessage: 'Saved Dashboards',
      description: 'Saved Dashboards navigation link',
    }),
    onClick: () => onNavLinkClick?.(RoutePathType.SavedDashboards),
    pathname: RoutePathType.SavedDashboards,
  });

  if (
    activeDashboard &&
    activeDashboard.name &&
    shouldShowSavedDashboardLink(activeDashboard)
  ) {
    dashboardSubNavs.push({
      label: activeDashboard.name,
      onClick: () => onNavLinkClick?.(activeDashboard.pathname),
      pathname: activeDashboard.pathname,
    });
  }
  return dashboardSubNavs as NavLink[];
};

const lookerDashboardPagePattern = /(documents|expirations|tickets|workflows)/g;

export const createLibrarySubNavLinks = (intl: IntlShape): NavLink[] => {
  return [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.playbooks',
        defaultMessage: 'Playbooks',
        description: 'Playbooks navigation link',
      }),
      pathname: '/playbooks',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.guidelines',
        defaultMessage: 'Guidelines',
        description: 'Guidelines navigation link',
      }),
      pathname: '/guidelines',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.clauses',
        defaultMessage: 'Clauses',
        description: 'Clauses navigation link',
      }),
      pathname: '/clause-library',
    },
  ];
};

const shouldShowSavedDashboardLink = (activeDashboard: DashboardAndPathname) =>
  activeDashboard?.pathname.includes('dashboards/') &&
  !activeDashboard?.pathname.match(lookerDashboardPagePattern) &&
  !activeDashboard.is_default;

export const shouldShowDocumentSubNavBar = (
  currentUser: User,
  pathname: string,
) =>
  !featureFlagIncluded(currentUser, 'WORKFLOW') &&
  (pathname.startsWith(RoutePathType.Documents) ||
    pathname.startsWith(RoutePathType.DocumentGroups) ||
    pathname.startsWith(RoutePathType.DocumentGroupsNew));

export const isSearchPathname = (pathname: string) =>
  pathname.startsWith(RoutePathType.Search) ||
  pathname.startsWith(RoutePathType.Analyzer) ||
  pathname.startsWith(RoutePathType.SavedSearches) ||
  pathname.startsWith(RoutePathType.DownloadExportReportPage);

export const shouldShowSearchSubNavbar = (
  currentUser: User,
  pathname: string,
) =>
  featureFlagIncluded(currentUser, FeatureFlagType.SearchV2) &&
  isSearchPathname(pathname);
